import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Rocket, CircleNotch, ArrowUp, CheckCircle, GearSix, Calendar, Compass,
  ArrowLeft, ChatCircleDots, MagnifyingGlass,
} from "@phosphor-icons/react";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";

const STATUS_COLS = [
  { k: "shipped",      label: "Shipped",      icon: CheckCircle, tone: "text-pos border-pos/40 bg-pos-soft/15" },
  { k: "in_progress",  label: "In Progress",  icon: GearSix,     tone: "text-mod border-mod/40 bg-mod-soft/15" },
  { k: "planned",      label: "Planned",      icon: Calendar,    tone: "text-info border-info/40 bg-info-soft/15" },
  { k: "under_review", label: "Under Review", icon: Compass,     tone: "text-secondary-fg border-subtle bg-surface" },
];

function ShippedDate({ iso }) {
  if (!iso) return null;
  const d = new Date(iso);
  return (
    <span className="text-[9px] font-mono uppercase tracking-widest text-muted-fg">
      · shipped {d.toLocaleDateString("en-IN", { day: "2-digit", month: "short" })}
    </span>
  );
}

export default function Roadmap() {
  const nav = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [items, setItems] = useState([]);
  const [myVotes, setMyVotes] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [pendingVote, setPendingVote] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [{ data }, votesResp] = await Promise.all([
        api.get("/roadmap"),
        user ? api.get("/roadmap/my-votes").catch(() => ({ data: { ids: [] } })) : Promise.resolve({ data: { ids: [] } }),
      ]);
      setItems(data.items || []);
      setMyVotes(new Set(votesResp.data?.ids || []));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!authLoading) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, user?.user_id]);

  const filtered = useMemo(() => {
    if (!filter.trim()) return items;
    const q = filter.toLowerCase();
    return items.filter(
      (it) =>
        it.title.toLowerCase().includes(q) ||
        (it.description || "").toLowerCase().includes(q) ||
        (it.category || "").toLowerCase().includes(q)
    );
  }, [items, filter]);

  const byStatus = useMemo(() => {
    const map = { shipped: [], in_progress: [], planned: [], under_review: [] };
    filtered.forEach((it) => {
      if (map[it.status]) map[it.status].push(it);
    });
    // Sort: shipped → newest first; rest → most votes, then "order".
    map.shipped.sort((a, b) => (b.shipped_at || "").localeCompare(a.shipped_at || ""));
    ["in_progress", "planned", "under_review"].forEach((k) => {
      map[k].sort((a, b) => (b.vote_count || 0) - (a.vote_count || 0) || (a.order || 0) - (b.order || 0));
    });
    return map;
  }, [filtered]);

  const toggleVote = async (id) => {
    if (!user) {
      nav("/login");
      return;
    }
    setPendingVote(id);
    // Optimistic
    const wasVoted = myVotes.has(id);
    setMyVotes((s) => {
      const next = new Set(s);
      if (wasVoted) next.delete(id); else next.add(id);
      return next;
    });
    setItems((arr) =>
      arr.map((it) =>
        it.id === id ? { ...it, vote_count: (it.vote_count || 0) + (wasVoted ? -1 : 1) } : it
      )
    );
    try {
      await api.post(`/roadmap/${id}/vote`);
    } catch {
      // Roll back
      setMyVotes((s) => {
        const next = new Set(s);
        if (wasVoted) next.add(id); else next.delete(id);
        return next;
      });
      setItems((arr) =>
        arr.map((it) =>
          it.id === id ? { ...it, vote_count: (it.vote_count || 0) + (wasVoted ? 1 : -1) } : it
        )
      );
    } finally {
      setPendingVote(null);
    }
  };

  return (
    <div className="min-h-screen bg-app text-primary-fg">
      <div className="max-w-6xl mx-auto px-4 py-8 sm:py-12">
        <Link
          to="/dashboard"
          className="inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest text-muted-fg hover:text-primary-fg transition mb-6"
        >
          <ArrowLeft size={11} weight="bold" />
          Back to Dashboard
        </Link>

        <div className="flex items-start gap-3 mb-1 flex-wrap">
          <Rocket size={22} weight="fill" className="text-pos mt-0.5" />
          <div className="flex-1 min-w-[250px]">
            <h1 className="text-2xl sm:text-3xl font-medium tracking-tight">Public Roadmap</h1>
            <p className="text-sm text-secondary-fg leading-relaxed mt-1 max-w-2xl">
              Yeh sab features queue me hain ya already ship ho chuke. Apne favourite
              pe vote karo — sabse demanded item next priority banta hai.
            </p>
          </div>
          <Link
            to="/feedback"
            data-testid="roadmap-suggest-link"
            className="px-3 py-2 text-[10px] font-mono uppercase tracking-widest border border-pos text-pos rounded-sm hover:bg-pos-soft/30 transition flex items-center gap-1.5"
          >
            <ChatCircleDots size={12} weight="fill" />
            Suggest Something
          </Link>
        </div>

        {/* Search */}
        <div className="relative mt-6 mb-8 max-w-md">
          <MagnifyingGlass size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-fg" />
          <input
            type="text"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Search features…"
            data-testid="roadmap-search"
            className="w-full bg-surface border border-subtle rounded-sm pl-9 pr-3 py-2 text-sm text-primary-fg placeholder-muted-fg/60 focus:outline-none focus:border-pos transition"
          />
        </div>

        {loading ? (
          <div className="flex items-center gap-2 py-16 text-muted-fg justify-center">
            <CircleNotch size={18} className="animate-spin" />
            <span className="text-xs font-mono uppercase tracking-widest">Loading roadmap…</span>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
            {STATUS_COLS.map((col) => {
              const Icon = col.icon;
              const list = byStatus[col.k] || [];
              return (
                <section
                  key={col.k}
                  data-testid={`roadmap-col-${col.k}`}
                  className="space-y-2"
                >
                  <div className={"flex items-center gap-1.5 p-2 border rounded-sm " + col.tone}>
                    <Icon size={13} weight="fill" />
                    <span className="text-[11px] font-mono uppercase tracking-widest">{col.label}</span>
                    <span className="ml-auto text-[10px] font-mono">{list.length}</span>
                  </div>
                  <AnimatePresence initial={false}>
                    {list.map((it) => (
                      <motion.div
                        layout
                        key={it.id}
                        initial={{ opacity: 0, y: 6 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0 }}
                        className="border border-subtle rounded-sm p-3 bg-surface/50 hover:border-pos/30 transition"
                        data-testid={`roadmap-card-${it.id}`}
                      >
                        <div className="flex items-start gap-2">
                          <div className="flex-1 min-w-0">
                            <div className="text-sm text-primary-fg font-medium leading-snug">
                              {it.title}
                            </div>
                            {it.description && (
                              <div className="text-[11px] text-secondary-fg leading-relaxed mt-1">
                                {it.description}
                              </div>
                            )}
                            <div className="text-[9px] font-mono uppercase tracking-widest text-muted-fg mt-2 flex items-center gap-1 flex-wrap">
                              {it.category && <span>· {it.category}</span>}
                              <ShippedDate iso={it.shipped_at} />
                            </div>
                          </div>
                          {/* Vote pill — disabled for shipped items */}
                          <button
                            type="button"
                            disabled={col.k === "shipped" || pendingVote === it.id}
                            onClick={() => toggleVote(it.id)}
                            data-testid={`roadmap-vote-${it.id}`}
                            title={
                              col.k === "shipped"
                                ? "Already shipped"
                                : myVotes.has(it.id)
                                ? "Click to remove your vote"
                                : "Upvote this feature"
                            }
                            className={
                              "shrink-0 flex flex-col items-center px-2 py-1.5 border rounded-sm text-[10px] font-mono transition " +
                              (col.k === "shipped"
                                ? "border-subtle text-muted-fg opacity-50 cursor-not-allowed"
                                : myVotes.has(it.id)
                                ? "border-pos bg-pos-soft/30 text-pos"
                                : "border-subtle text-secondary-fg hover:border-pos/60 hover:text-pos")
                            }
                          >
                            <ArrowUp size={11} weight={myVotes.has(it.id) ? "fill" : "bold"} />
                            <span className="tabular-nums mt-0.5">{it.vote_count || 0}</span>
                          </button>
                        </div>
                      </motion.div>
                    ))}
                  </AnimatePresence>
                  {list.length === 0 && (
                    <div className="text-[11px] text-muted-fg italic px-2 py-3">
                      Nothing here yet.
                    </div>
                  )}
                </section>
              );
            })}
          </div>
        )}

        <div className="mt-10 text-[10px] text-muted-fg/70 italic text-center max-w-xl mx-auto">
          Don't see your idea? Open the suggestion form — every vote and every
          suggestion is read personally.
        </div>
      </div>
    </div>
  );
}
